import { z } from "zod"
import { ChannelEnum, FlowEnum } from "../../campaigns/commonTypes"
import { TimeFrameEnum, TimeFrameSchema } from "../../insights/timeFrame"
import { PaginationSchema } from "../../shared/pagination_dtos"

export const ConversationExperimentProductionName = "production"

export const PromptExperimentReasoningEffortEnum = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
] as const

export const PromptExperimentVerbosityEnum = ["low", "medium", "high"] as const

const optionalTrimmedString = z
  .union([z.literal("").transform(() => undefined), z.string().trim().min(1)])
  .optional()

export const PromptExperimentConfigurationSchema = z.object({
  model: z.string().min(1),
  reasoningEffort: z.enum(PromptExperimentReasoningEffortEnum).optional(),
  verbosity: z.enum(PromptExperimentVerbosityEnum).optional(),
  promptName: z.string().min(1).optional(),
  tag: optionalTrimmedString,
})

export const LaunchBackofficePromptExperimentSchema = z.object({
  name: optionalTrimmedString,
  promptName: z.string().min(1),
  datasetTag: optionalTrimmedString,
  sample: z.coerce.number().int().positive().max(1000),
  concurrency: z.coerce.number().int().positive().max(20).optional(),
  reruns: z.coerce.number().int().positive().max(20).optional(),
  experiments: z.array(PromptExperimentConfigurationSchema).min(1).max(10),
})

export const GetBackofficePromptExperimentRunsSchema = z.object({
  limit: z.coerce.number().int().positive().max(100).default(20),
})

const booleanQueryParam = z
  .union([z.boolean(), z.literal("true"), z.literal("false")])
  .optional()
  .transform((value) => {
    if (value === undefined) return undefined
    if (typeof value === "boolean") return value
    return value === "true"
  })

const multiValueQueryParam = <T extends z.ZodType>(item: T) =>
  z.preprocess((value) => {
    if (value == null || value === "") return undefined
    return Array.isArray(value) ? value : [value]
  }, z.array(item).optional())

export const BackofficeConversationExperimentConversationFiltersSchema = z.object({
  hasQuestions: booleanQueryParam,
  hasTicket: booleanQueryParam,
  campaignFlow: z.enum(FlowEnum).optional(),
  companyIds: multiValueQueryParam(z.string().regex(/^[1-9]\d*$/)),
  channels: multiValueQueryParam(z.enum(ChannelEnum)),
  locales: multiValueQueryParam(z.string().min(1)),
})

export const GetBackofficeConversationExperimentsSchema = z.object({
  ...PaginationSchema.shape,
  ...BackofficeConversationExperimentConversationFiltersSchema.shape,
  excludeProduction: booleanQueryParam.default(true),
  experiment: z.string().optional(),
  experimentDomain: z.string().min(1).optional(),
  timeFrame: TimeFrameSchema.default(TimeFrameEnum.Last3Days),
})

export const GetBackofficeConversationExperimentAnalyticsSchema = z.object({
  ...BackofficeConversationExperimentConversationFiltersSchema.shape,
  experimentDomain: z.string().min(1).optional(),
  timeFrame: TimeFrameSchema.default(TimeFrameEnum.Last3Days),
})

export const GetBackofficeConversationExperimentNamesSchema = z.object({
  experimentDomain: z.string().min(1).optional(),
})
